/**
 * Listing Page
 *
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 *
 * Purpose:
 * Property search and listing page. Displays filtered property results based on search criteria.
 * Integrates with SearchBarContext for date, guest, and bedroom filters.
 *
 * Features:
 * - Property grid with filtering
 * - Search bar integration
 * - Sort options (price, rating, newest)
 * - Filter sidebar (price range, amenities, property type)
 * - Pagination or infinite scroll
 * - Responsive layout
 * - Analytics tracking
 *
 * Components Used:
 * - SearchBar (search interface)
 * - PropertyCard (property listings)
 * - PriceRangeSlider (price filtering)
 * - Footer (site footer)
 *
 * Dependencies:
 * - React 19.2.4 (hooks)
 * - React Router 7 (navigation)
 * - SearchBarContext (search state)
 * - TailwindCSS 4.1 (responsive styling)
 *
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation
 * - 2026-03-05: Normalized eyebrow text to shared ui-eyebrow-muted
 * - Previous: Mixed font-bold text-sm and font-black text-xs styles
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PropertyCard } from '../components/PropertyCard';
import { PropertyMap } from '../components/PropertyMap';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { ProIcon } from '../components/ProIcon';
import { useSearchBar } from '../contexts/SearchBarContext';
import { useModal } from '../contexts/ModalContext';
import { PriceRangeSlider } from '../components/PriceRangeSlider';
import { trackViewItemList } from '../lib/analytics';
import { getFavorites } from '../lib/favorites';

import { MOCK_PROPERTIES, toPropertyCardFormat } from '../mock/index';

export const Listing = () => {
  const location = useLocation();
  const { searchParams } = useSearchBar();
  const { openModal, closeModal } = useModal();
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<any[]>([]);
  const [view, setView] = useState<'list' | 'split' | 'map'>('split');
  const [activeFilters, setActiveFilters] = useState({
    priceMin: 0,
    priceMax: 5000,
    type: 'all',
    amenities: [] as string[],
    sort: 'recommended'
  });
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  // Register filters modal with global modal context
  useEffect(() => {
    if (showFiltersModal) {
      openModal('filters');
    } else {
      closeModal('filters');
    }

    // Cleanup on unmount
    return () => closeModal('filters');
  }, [showFiltersModal, openModal, closeModal]);
  const showFavoritesOnly = new URLSearchParams(location.search).get('favorites') === '1';

  useEffect(() => {
    // Use centralized mock data
    const properties = MOCK_PROPERTIES.map(toPropertyCardFormat);
    setAllProperties(properties);
    setFilteredProperties(properties);
  }, []);

  // Derive dynamic filter options from data
  const propertyTypeOptions = ['all', ...Array.from(new Set(allProperties.flatMap(p => p.badges)))].slice(0, 6);
  const amenityOptions = Array.from(new Set(allProperties.flatMap(p => p.amenities)))
    .map(a => a.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '))
    .slice(0, 8);

  useEffect(() => {
    let result = [...allProperties];

    // Favorites filter via URL param
    if (showFavoritesOnly) {
      const favorites = getFavorites();
      result = result.filter((p) => favorites.includes(p.id));
    }

    // Price Filter
    result = result.filter(p => p.price >= activeFilters.priceMin && p.price <= activeFilters.priceMax);


    // Type Filter - check if selection matches any badge
    if (activeFilters.type !== 'all') {
      result = result.filter(p => p.badges.includes(activeFilters.type));
    }

    // Amenities Filter - match the formatted labels
    if (activeFilters.amenities.length > 0) {
      result = result.filter(p =>
        activeFilters.amenities.every(selectedLabel =>
          p.amenities.some((a: string) =>
            a.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') === selectedLabel
          )
        )
      );
    }

    // Sorting
    if (activeFilters.sort === 'price-low') result.sort((a, b) => a.price - b.price);
    if (activeFilters.sort === 'price-high') result.sort((a, b) => b.price - a.price);
    if (activeFilters.sort === 'bedrooms') result.sort((a, b) => b.bedrooms - a.bedrooms);

    setFilteredProperties(result);
  }, [activeFilters, allProperties, showFavoritesOnly]);

  useEffect(() => {
    if (filteredProperties.length === 0) return;

    trackViewItemList(
      'destinations_results',
      filteredProperties.slice(0, 20).map((property: any) => ({
        id: property.id,
        name: property.title,
        price: property.price,
        category: property.badges?.[0],
      }))
    );
  }, [filteredProperties]);

  const toggleFilter = (category: string, value: any) => {
    setActiveFilters(prev => ({
      ...prev,
      [category]: prev[category as keyof typeof prev] === value ? (category === 'bedrooms' ? 0 : 'all') : value
    }));
  };

  const updatePriceRange = (min: number, max: number) => {
    setActiveFilters(prev => ({
      ...prev,
      priceMin: min,
      priceMax: max
    }));
  };

  const hasSavedFavorites = getFavorites().length > 0;

  const newsletterSource = showFavoritesOnly && hasSavedFavorites
    ? 'listing_favorites'
    : filteredProperties.length === 0
      ? 'listing_no_results'
      : 'listing_results';

  const newsletterTitle = showFavoritesOnly && hasSavedFavorites
    ? 'Want Last-Minute Deals On Homes Like These?'
    : filteredProperties.length === 0
      ? 'Get Last-Minute Marbella Deals While You Keep Looking'
      : 'Get Last-Minute Marbella Rental Deals';

  const newsletterDescription = showFavoritesOnly && hasSavedFavorites
    ? 'You already have strong options saved. We can also send newly opened dates, price drops, and direct-booking offers for similar Marbella rentals.'
    : filteredProperties.length === 0
      ? 'If your exact match is not live right now, join the list and we will send newly opened dates, price drops, and direct-booking offers worth checking.'
      : 'Not ready to book today? Join the list for newly opened dates, price drops, and direct-booking offers across inspected Marbella rentals.';

  const newsletterEyebrow = showFavoritesOnly && hasSavedFavorites
    ? 'Saved Rentals'
    : filteredProperties.length === 0
      ? 'Fresh Openings'
      : 'Soft Option';

  const newsletterButtonLabel = showFavoritesOnly && hasSavedFavorites
    ? 'Send Similar Deals'
    : filteredProperties.length === 0
      ? 'Email Me Deals'
      : 'Get Deal Alerts';

  const newsletterTags = showFavoritesOnly && hasSavedFavorites
    ? ['listing', 'favorites']
    : filteredProperties.length === 0
      ? ['listing', 'browse-recovery']
      : ['listing', 'browse-recovery'];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-16">
      {/* Dropdown Overlay */}
      {openDropdown && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setOpenDropdown(null)}
        />
      )}

      {/* Mobile Filters Modal */}
      {showFiltersModal && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowFiltersModal(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-black text-slate-900">Filters</h2>
              <button
                onClick={() => setShowFiltersModal(false)}
                className="ui-btn-icon w-9 h-9 border-none"
              >
                <ProIcon icon="chevron-down" family="light" className="w-5 h-5 rotate-180 text-slate-400" />
              </button>
            </div>

            {/* Mobile Filter Content */}
            <div className="space-y-6">
              {/* Price Range */}
              <div>
                <label className="ui-eyebrow-muted">Price Range / Night</label>
                <div className="mt-3">
                  <PriceRangeSlider
                    min={0}
                    max={5000}
                    valueMin={activeFilters.priceMin}
                    valueMax={activeFilters.priceMax}
                    onChange={(min, max) => updatePriceRange(min, max)}
                  />
                  <div className="flex justify-between mt-2">
                    <span className="ui-caption-strong">€{activeFilters.priceMin}/night</span>
                    <span className="ui-caption-strong">€{activeFilters.priceMax}/night</span>
                  </div>
                </div>
              </div>


              {/* Property Type */}
              <div>
                <label className="ui-eyebrow-muted">Property Type</label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {propertyTypeOptions.map(t => (
                    <button
                      key={t}
                      onClick={() => setActiveFilters(prev => ({ ...prev, type: t }))}
                      className={`px-4 py-2 rounded-xl text-sm font-bold tracking-normal transition-all ${activeFilters.type === t
                          ? 'bg-brand-ocean text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                    >
                      {t === 'all' ? 'All' : t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amenities */}
              <div>
                <label className="ui-eyebrow-muted">Amenities</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {amenityOptions.map(a => (
                    <button
                      key={a}
                      onClick={() => {
                        const newAmenities = activeFilters.amenities.includes(a)
                          ? activeFilters.amenities.filter(item => item !== a)
                          : [...activeFilters.amenities, a];
                        setActiveFilters(prev => ({ ...prev, amenities: newAmenities }));
                      }}
                      className={`px-3 py-1.5 rounded-lg ui-button-text transition-all ${activeFilters.amenities.includes(a)
                          ? 'bg-brand-ocean text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setActiveFilters({ priceMin: 0, priceMax: 5000, type: 'all', amenities: [], sort: 'recommended' })}
                  className="ui-btn-muted flex-1 px-4 py-3 text-sm font-bold tracking-normal"
                >
                  Reset
                </button>
                <button
                  onClick={() => setShowFiltersModal(false)}
                  className="ui-btn-primary flex-1 px-4 py-3 text-sm font-bold tracking-normal"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="sticky top-16 z-40 bg-white border-b border-slate-100 py-3 sm:py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
          {/* Mobile Filters Button */}
          <button
            onClick={() => setShowFiltersModal(true)}
            className="md:hidden w-full flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm"
          >
            <div className="flex items-center gap-3">
              <ProIcon icon="sliders" family="light" className="w-5 h-5 text-slate-400" />
              <span className="font-bold text-slate-900">Filters</span>
            </div>
            {(activeFilters.priceMin > 0 || activeFilters.priceMax < 5000 || activeFilters.type !== 'all' || activeFilters.amenities.length > 0) && (
              <span className="bg-brand-ocean text-white ui-eyebrow px-2 py-1 rounded-full">
                {[
                  activeFilters.priceMin > 0 || activeFilters.priceMax < 5000 ? 1 : 0,
                  activeFilters.type !== 'all' ? 1 : 0,
                  activeFilters.amenities.length > 0 ? activeFilters.amenities.length : 0
                ].reduce((a, b) => a + b, 0)}
              </span>
            )}
          </button>

          {/* Desktop Filters */}
          <div className="hidden md:flex items-center gap-2 flex-wrap w-full md:w-auto relative">

            {/* Price Range */}
            <div className="relative">
              <FilterButton
                label={`€${activeFilters.priceMin} - €${activeFilters.priceMax}/night`}
                isActive={activeFilters.priceMin > 0 || activeFilters.priceMax < 5000}
                onClick={() => setOpenDropdown(openDropdown === 'price' ? null : 'price')}
              />
              {openDropdown === 'price' && (
                <div className="absolute top-full left-0 mt-2 w-80 ui-card-elevated p-6 z-50">
                  <div className="space-y-4">
                    <div>
                      <label className="ui-eyebrow-muted">Price Range / Night</label>
                      <div className="mt-3">
                        <PriceRangeSlider
                          min={0}
                          max={5000}
                          valueMin={activeFilters.priceMin}
                          valueMax={activeFilters.priceMax}
                          onChange={(min, max) => updatePriceRange(min, max)}
                        />
                        <div className="flex justify-between mt-2">
                          <span className="ui-caption-strong">€{activeFilters.priceMin}/night</span>
                          <span className="ui-caption-strong">€{activeFilters.priceMax}/night</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        onClick={() => updatePriceRange(0, 5000)}
                        className="text-sm font-bold tracking-normal text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => setOpenDropdown(null)}
                        className="ui-btn-primary px-4 py-2 rounded-xl text-sm font-bold tracking-normal"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Property Type */}
            <div className="relative">
              <FilterButton
                label={activeFilters.type === 'all' ? "Property Type" : activeFilters.type}
                isActive={activeFilters.type !== 'all'}
                onClick={() => setOpenDropdown(openDropdown === 'type' ? null : 'type')}
              />
              {openDropdown === 'type' && (
                <div className="absolute top-full left-0 mt-2 w-72 ui-card-elevated p-4 z-50">
                  <div className="space-y-3">
                    <label className="ui-eyebrow-muted">Property Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {propertyTypeOptions.map(t => (
                        <button
                          key={t}
                          onClick={() => { setActiveFilters(prev => ({ ...prev, type: t })); }}
                          className={`px-4 py-2 rounded-xl text-sm font-bold tracking-normal transition-all ${activeFilters.type === t
                              ? 'bg-brand-ocean text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                          {t === 'all' ? 'All' : t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                    <button
                      onClick={() => setOpenDropdown(null)}
                      className="ui-btn-primary px-4 py-2 rounded-xl text-sm font-bold tracking-normal"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Amenities */}
            <div className="relative">
              <FilterButton
                label={activeFilters.amenities.length === 0 ? "Amenities" : `${activeFilters.amenities.length} Selected`}
                isActive={activeFilters.amenities.length > 0}
                onClick={() => setOpenDropdown(openDropdown === 'amenities' ? null : 'amenities')}
              />
              {openDropdown === 'amenities' && (
                <div className="absolute top-full left-0 mt-2 w-80 ui-card-elevated p-4 z-50">
                  <div className="space-y-3">
                    <label className="ui-eyebrow-muted">Amenities</label>
                    <div className="flex flex-wrap gap-2">
                      {amenityOptions.map(a => (
                        <button
                          key={a}
                          onClick={() => {
                            const newAmenities = activeFilters.amenities.includes(a)
                              ? activeFilters.amenities.filter(item => item !== a)
                              : [...activeFilters.amenities, a];
                            setActiveFilters(prev => ({ ...prev, amenities: newAmenities }));
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-bold tracking-normal transition-all ${activeFilters.amenities.includes(a)
                              ? 'bg-brand-ocean text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
                    <button
                      onClick={() => setActiveFilters(prev => ({ ...prev, amenities: [] }))}
                      className="text-sm font-bold tracking-normal text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => setOpenDropdown(null)}
                      className="ui-btn-primary px-4 py-2 rounded-xl text-sm font-bold tracking-normal"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setActiveFilters({ priceMin: 0, priceMax: 5000, type: 'all', amenities: [], sort: 'recommended' })}
              className="ui-btn-icon p-3 flex items-center gap-2"
              title="Reset Filters"
            >
              <ProIcon icon="sliders" family="light" className="w-5 h-5 text-slate-400" />
              {(activeFilters.priceMin > 0 || activeFilters.priceMax < 5000 || activeFilters.type !== 'all' || activeFilters.amenities.length > 0) && (
                <span className="text-sm font-bold tracking-normal text-slate-500">Reset</span>
              )}
            </button>
          </div>

          {/* View Toggle - Mobile and Desktop */}
          <div className="flex sm:hidden items-center gap-1 bg-slate-100 p-1 rounded-xl w-full">
            <button
              onClick={() => setView('list')}
              className={`flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold tracking-normal transition-all flex-1 ${view === 'list' || view === 'split' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-600 hover:text-slate-700'}`}
            >
              <ProIcon icon="chart-tree-map" family="solid" className="w-3.5 h-3.5" />
              LIST
            </button>
            <button
              onClick={() => setView('map')}
              className={`flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold tracking-normal transition-all flex-1 ${view === 'map' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-600 hover:text-slate-700'}`}
            >
              <ProIcon icon="map" family="solid" className="w-3.5 h-3.5" />
              MAP
            </button>
          </div>

          {/* Desktop View Toggle - 3 options */}
          <div className="hidden sm:flex items-center gap-1 bg-slate-100 p-1 rounded-xl flex-shrink-0">
            <button
              onClick={() => setView('list')}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold tracking-normal transition-all whitespace-nowrap ${view === 'list' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-600 hover:text-slate-700'}`}
            >
              <ProIcon icon="chart-tree-map" family="solid" className="w-3.5 h-3.5" />
              LIST
            </button>
            <button
              onClick={() => setView('split')}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold tracking-normal transition-all whitespace-nowrap ${view === 'split' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-600 hover:text-slate-700'}`}
            >
              <ProIcon icon="map-location-dot" family="solid" className="w-3.5 h-3.5" />
              BOTH
            </button>
            <button
              onClick={() => setView('map')}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold tracking-normal transition-all whitespace-nowrap ${view === 'map' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-600 hover:text-slate-700'}`}
            >
              <ProIcon icon="map" family="solid" className="w-3.5 h-3.5" />
              MAP
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col lg:flex-row relative">
        {/* List View */}
        <div className={`${view === 'map' ? 'hidden' : view === 'split' ? 'flex-1 w-full lg:w-[55%]' : 'w-full'} relative`}>
          <div className={view === 'split' ? 'pl-4 sm:pl-6 lg:pl-[max(2rem,calc((100vw-80rem)/2+2rem))] pr-4 sm:pr-6 py-6 sm:py-8' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8'}>
            {/* Header - Always visible even in split view */}
            <div className="mb-6 sm:mb-12 flex flex-col sm:flex-row justify-between sm:items-end gap-4">
              <div className="space-y-1 sm:space-y-2">
                <h1 className="ui-title-compact uppercase">
                  {showFavoritesOnly ? 'Your Favorite Rentals' : 'Inspected Rentals Across Marbella'}
                </h1>
                <p className="ui-eyebrow-muted">
                  {showFavoritesOnly
                    ? `${filteredProperties.length} saved rentals`
                    : `${filteredProperties.length} homes personally checked by our Marbella team`}
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 text-sm font-bold tracking-normal text-slate-600 whitespace-nowrap">
                Sort by:
                <select
                  value={activeFilters.sort}
                  onChange={(e) => setActiveFilters(prev => ({ ...prev, sort: e.target.value }))}
                  className="text-sm font-bold text-slate-900 bg-transparent border border-slate-200 rounded-lg px-3 py-2 min-h-[40px] cursor-pointer hover:border-brand-ocean transition-colors focus:outline-none focus:ring-2 focus:ring-brand-ocean/20 focus:border-brand-ocean appearance-auto"
                >
                  <option value="recommended">Recommended</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="bedrooms">Most Bedrooms</option>
                </select>
              </div>
            </div>

            {/* Property Grid - 1 col in split mode, 2 cols in list mode */}
            {/* Keep a softer capture option on browse-heavy states, especially favorites and no-results */}
            {filteredProperties.length === 0 ? (
              <div className="space-y-6">
                <div className="ui-card-elevated p-6 sm:p-8 text-center">
                  <p className="ui-eyebrow-brand mb-3">No Exact Match Yet</p>
                  <h2 className="text-2xl sm:text-3xl font-black text-brand-ocean tracking-tight">
                    No rentals match those filters right now.
                  </h2>
                  <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
                    Reset the filters or join the list for last-minute openings and price drops while new homes are added.
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() =>
                        setActiveFilters({
                          priceMin: 0,
                          priceMax: 5000,
                          type: 'all',
                          amenities: [],
                          sort: 'recommended',
                        })
                      }
                      className="ui-btn-secondary"
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>

                <NewsletterSignup
                  variant="inline"
                  source={newsletterSource}
                  eyebrow={newsletterEyebrow}
                  title={newsletterTitle}
                  description={newsletterDescription}
                  buttonLabel={newsletterButtonLabel}
                  favoritesCount={getFavorites().length}
                  tags={newsletterTags}
                />
              </div>
            ) : (
              <>
                <div className={`grid gap-6 sm:gap-8 ${view === 'split' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                  {filteredProperties.map((prop: any) => (
                    <PropertyCard key={prop.id} property={prop} showTotalPrice={true} />
                  ))}
                </div>

                <div className="mt-8 sm:mt-10">
                  <NewsletterSignup
                    variant="inline"
                    source={newsletterSource}
                    eyebrow={newsletterEyebrow}
                    title={newsletterTitle}
                    description={newsletterDescription}
                    buttonLabel={newsletterButtonLabel}
                    favoritesCount={showFavoritesOnly && hasSavedFavorites ? filteredProperties.length : getFavorites().length}
                    tags={newsletterTags}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Map View */}
        <div className={`${view === 'list' ? 'hidden' : view === 'split' ? 'w-full lg:w-[45%] bg-slate-200 border-slate-200 lg:border-l lg:sticky lg:top-[calc(4rem+3.5rem)] h-[350px] sm:h-[450px] lg:h-[calc(100vh-4rem-3.5rem)] mt-4 lg:mt-0' : 'w-full h-[calc(100vh-8rem)] sm:h-[calc(100vh-7.5rem)]'} overflow-hidden relative z-0`}>
          <PropertyMap
            key={`listing-map-${view}`}
            properties={filteredProperties}
          />
        </div>
      </div>
    </div>
  );
};

const FilterButton = ({ label, icon, isActive, onClick }: { label: string, icon?: React.ReactNode, isActive?: boolean, onClick?: () => void }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border transition-all whitespace-nowrap shadow-sm text-xs font-bold tracking-normal uppercase flex-shrink-0 ${isActive
        ? 'bg-brand-ocean border-brand-ocean text-white hover:opacity-90'
        : 'bg-white border-slate-200 text-slate-600 hover:border-brand-ocean hover:text-brand-ocean'
      }`}
  >
    {label}
    {icon ? icon : <ProIcon icon="chevron-down" family="light" className={`w-4 h-4 transition-transform ${isActive ? 'rotate-180' : 'opacity-50'}`} />}
  </button>
);
